import React, { useState, useRef, useEffect } from 'react';
import { Download, Eye, X, BookOpen } from 'lucide-react';
import axios from 'axios';

const Employee360PensionScrollCard = ({ employeeId, profile, loading }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [pensionScrollData, setPensionScrollData] = useState(null);
  const [errorState, setErrorState] = useState(null);
  const [cardLoading, setCardLoading] = useState(true);
  const printRef = useRef(null);

  useEffect(() => {
    const fetchPensionScroll = async () => {
      setCardLoading(true);
      setErrorState(null);
      try {
        const res = await axios.get(`/v1/employee-master/${employeeId}/pension-scroll`);
        setPensionScrollData(res.data);
      } catch (err) {
        console.error("Failed to fetch pension scroll report", err);
        const detail = err?.response?.data?.detail || "Failed to load report data";
        setErrorState(detail);
      } finally {
        setCardLoading(false);
      }
    };
    fetchPensionScroll();
  }, [employeeId]);

  const loadHtml2Pdf = async () => {
    if (!window.html2pdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
  };

  const handleDownloadPDF = async () => {
    const printContent = printRef.current;
    if (!printContent) return;
    await loadHtml2Pdf();
    
    // Diagnostic checks (as requested by diagnostic guidelines)
    console.log("PDF Capture Diagnostics:", {
      element: printContent,
      innerHTML_length: printContent.innerHTML?.length || 0,
      clientWidth: printContent.clientWidth,
      clientHeight: printContent.clientHeight,
      scrollWidth: printContent.scrollWidth,
      scrollHeight: printContent.scrollHeight,
      innerText: printContent.innerText ? printContent.innerText.slice(0, 100) : ''
    });

    await document.fonts.ready;
    await new Promise(resolve => requestAnimationFrame(() => resolve()));

    const opt = {
      margin: 10,
      filename: `Pension_Scroll_${employeeId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        scrollX: 0,
        scrollY: 0
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'landscape'
      }
    };
    window.html2pdf().set(opt).from(printContent).save();
  };

  if (loading || cardLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg animate-pulse">
            <BookOpen size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Pension Scroll</h3>
            <p className="text-sm text-gray-500">Loading Pension Scroll report data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (errorState) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6 shadow-sm flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-red-50 text-red-600 rounded-lg">
            <BookOpen size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-red-900 border-red-200">Pension Scroll - Issue Detected</h3>
            <p className="text-sm text-red-600 font-medium">Configure Required Fields: {errorState}</p>
          </div>
        </div>
      </div>
    );
  }

  const psd = pensionScrollData;
  if (!psd) return null;

  const formatDateDot = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      return `${day}-${month}-${d.getFullYear()}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
            <BookOpen size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Pension Scroll</h3>
            <p className="text-sm text-gray-500">View and download the complete Pension Scroll register sheet.</p>
          </div>
        </div>
        <button 
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-100 font-medium transition-colors"
        >
          <Eye size={18} />
          Preview
        </button>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-gray-50 rounded-xl shadow-2xl w-[1200px] max-w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-800">Pension Scroll Preview</h2>
              <div className="flex gap-3 items-center">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button 
                  onClick={() => setShowPreview(false)} 
                  className="p-2 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1 bg-white">
              <div ref={printRef} className="w-full text-black bg-white p-4 font-serif text-[12px] leading-relaxed">
                
                {/* Pune Cantonment Board Header */}
                <div className="text-center mb-6">
                  <div className="font-bold text-base tracking-widest uppercase">PUNE CANTONMENT BOARD</div>
                  <div className="font-bold text-lg tracking-wide uppercase border-b-2 border-black pb-1 inline-block">PENSION SCROLL</div>
                </div>

                {/* Table structure */}
                <table className="w-full border-collapse border border-black text-left">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-black p-2 text-center w-[5%] font-bold">SR. NO</th>
                      <th className="border border-black p-2 w-[25%] font-bold">NAME DESIGNATION ADDRESS OF ELIGIBLE SERVANT</th>
                      <th className="border border-black p-2 w-[10%] font-bold text-center">DATE OF APPOINTMENT</th>
                      <th className="border border-black p-2 w-[10%] font-bold text-center">DATE OF CONFIRMATION</th>
                      <th className="border border-black p-2 w-[10%] font-bold text-center">
                        {psd.group === "GROUP_2" ? "DATE OF EXPIRED" : "DATE OF RETIRED"}
                      </th>
                      <th className="border border-black p-2 w-[10%] font-bold text-center">PENSION PER MONTH</th>
                      <th className="border border-black p-2 w-[10%] font-bold text-center">D.C.R.G.</th>
                      <th className="border border-black p-2 w-[15%] font-bold">FAMILY PENSION</th>
                      <th className="border border-black p-2 w-[10%] font-bold text-center">REMARKS PROVISIONAL GRATUITY</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-black p-2 text-center valign-top">1</td>
                      <td className="border border-black p-2 valign-top font-sans">
                        <div className="font-bold uppercase">{psd.eligible_servant.name}, {psd.eligible_servant.designation}</div>
                        <div className="mt-1 text-gray-700 italic">
                          {psd.eligible_servant.status_label} {formatDateDot(psd.eligible_servant.status_date)}
                          {psd.group === "GROUP_2" && psd.eligible_servant.nominee_name && (
                            <span>, Family Pension payable to his Wife {psd.eligible_servant.nominee_name}</span>
                          )}
                        </div>
                        {psd.eligible_servant.address && (
                          <div className="mt-2 text-xs font-normal">
                            Residing at: {psd.eligible_servant.address}
                          </div>
                        )}
                      </td>
                      <td className="border border-black p-2 text-center valign-top">{formatDateDot(psd.date_of_appointment)}</td>
                      <td className="border border-black p-2 text-center valign-top">{formatDateDot(psd.date_of_confirmation)}</td>
                      <td className="border border-black p-2 text-center valign-top">{formatDateDot(psd.eligible_servant.status_date)}</td>
                      <td className="border border-black p-2 text-center valign-top font-bold">{psd.pension_per_month.display}</td>
                      <td className="border border-black p-2 text-center valign-top font-bold">{psd.dcrg.display}</td>
                      <td className="border border-black p-2 valign-top whitespace-pre-line font-mono">{psd.family_pension.display}</td>
                      <td className="border border-black p-2 text-center valign-top font-bold">{psd.provisional_gratuity.display}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Footer signatures */}
                <div className="mt-16 grid grid-cols-3 text-center font-sans font-semibold text-[11px] gap-8">
                  <div>
                    <div>CHIEF ACCOUNTANT</div>
                    <div>PCB PUNE</div>
                  </div>
                  <div>
                    <div>CHIEF EXECUTIVE OFFICER, PUNE</div>
                    <div>Dt. ________________</div>
                  </div>
                  <div>
                    <div>BRIG</div>
                    <div>PRESIDENT CANTONMENT BOARD</div>
                    <div>Dt. ________________</div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Employee360PensionScrollCard;
